import { useEffect, useMemo, useState } from 'react';

import { useConversations } from '../../hooks/use-conversations';
import { ResizableListPane } from '../../shell/ResizableListPane';

import { ConversationListItem } from './ConversationListItem';
import { ConversationThread } from './ConversationThread';

interface Props {
  /** Tab payload — if a conversationId was opened directly, start on it. */
  conversationId?: string | undefined;
  /** Open a new tab — populated by the Workspace tab system. */
  openTab?: (
    viewId: string,
    payload: Record<string, unknown>,
    label?: string,
    opts?: { ephemeral?: boolean; pinned?: boolean },
  ) => void;
}

export function ConversationsView({ conversationId, openTab }: Props = {}) {
  const {
    status,
    conversations,
    error,
    selectedId,
    selectConversation,
    createConversation,
    markRead,
    refreshConversation,
    setCreatorMode,
    renameConversation,
  } = useConversations();

  const [filter, setFilter] = useState('');

  // Sync incoming tab payload on first render (when a pop-out tab opens).
  useEffect(() => {
    if (conversationId && selectedId !== conversationId) {
      selectConversation(conversationId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  // Auto-select first conversation when the list loads.
  useEffect(() => {
    if (!selectedId && conversations.length > 0) {
      const first = conversations[0];
      if (first) selectConversation(first.id);
    }
  }, [conversations, selectedId, selectConversation]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return conversations;
    return conversations.filter(
      (c) =>
        c.title.toLowerCase().includes(q) || (c.preview ?? '').toLowerCase().includes(q),
    );
  }, [conversations, filter]);

  const selected = conversations.find((c) => c.id === selectedId) ?? null;

  const handleSelect = (id: string) => {
    selectConversation(id);
    void markRead(id);
  };

  const handlePopOut = (id: string, title: string) => {
    if (!openTab) return;
    openTab('conversation-detail', { conversationId: id }, title.slice(0, 32), {
      ephemeral: false,
    });
  };

  return (
    <div style={{ display: 'flex', height: '100%', minHeight: 0, background: 'var(--bg)' }}>
      {/* List pane — resizable; width persists per-view. */}
      <ResizableListPane
        storageKey="conversations"
        defaultWidth={320}
        minWidth={240}
        maxWidth={560}
        paneStyle={{
          borderRight: '1px solid var(--border)',
          overflowY: 'auto',
          background: 'var(--bg-alt)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            padding: '12px 14px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexShrink: 0,
            position: 'sticky',
            top: 0,
            background: 'var(--bg-alt)',
            zIndex: 1,
          }}
        >
          <input
            placeholder="Filter conversations…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{
              flex: 1,
              minWidth: 0,
              height: 30,
              boxSizing: 'border-box',
              padding: '0 10px',
              fontSize: 12,
              border: '1px solid var(--border-strong)',
              borderRadius: 6,
              background: 'var(--surface)',
              outline: 'none',
              color: 'var(--text)',
              fontFamily: 'inherit',
            }}
          />
          <button
            type="button"
            onClick={() => void createConversation()}
            style={{
              // Match the input's box exactly (same height + box model + a
              // transparent border so the 1px doesn't shift the baseline), and
              // never shrink/wrap so "+ New" stays crisp at min list width.
              height: 30,
              boxSizing: 'border-box',
              flexShrink: 0,
              padding: '0 12px',
              fontSize: 12,
              background: 'var(--accent)',
              color: '#fff',
              border: '1px solid var(--accent)',
              borderRadius: 6,
              fontWeight: 500,
              whiteSpace: 'nowrap',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            + New
          </button>
        </div>
        {status === 'loading' && conversations.length === 0 && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--text-mute)' }}>Loading…</div>
        )}
        {status === 'error' && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--kind-failure)' }}>
            {error ?? 'Failed to load conversations.'}
          </div>
        )}
        {status === 'success' && filtered.length === 0 && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--text-mute)' }}>
            {filter ? 'No conversations match that filter.' : 'No conversations yet.'}
          </div>
        )}
        {filtered.map((c) => (
          <ConversationListItem
            key={c.id}
            item={c}
            selected={c.id === selectedId}
            onSelect={() => handleSelect(c.id)}
            onPopOut={() => handlePopOut(c.id, c.title)}
          />
        ))}
      </ResizableListPane>

      {/* Preview pane */}
      <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
        {selected ? (
          <ConversationThread
            conversation={selected}
            onPopOut={() => handlePopOut(selected.id, selected.title)}
            onStreamComplete={refreshConversation}
            onSetCreatorMode={setCreatorMode}
            onRename={renameConversation}
          />
        ) : (
          <EmptyHint
            title="Select a conversation"
            hint="Single-click previews it here; double-click opens it as its own tab."
          />
        )}
      </div>
    </div>
  );
}

/** Pop-out tab entry — renders a single conversation standalone (no list). */
export function ConversationDetailView({ conversationId }: { conversationId?: string | undefined }) {
  const { conversations, refreshConversation, setCreatorMode, renameConversation } = useConversations();
  const conv = conversations.find((c) => c.id === conversationId) ?? null;
  if (!conv) {
    return (
      <EmptyHint
        title="Conversation unavailable"
        hint="This conversation was closed or isn't in the current list."
      />
    );
  }
  return (
    <ConversationThread
      conversation={conv}
      onStreamComplete={refreshConversation}
      onSetCreatorMode={setCreatorMode}
      onRename={renameConversation}
    />
  );
}

function EmptyHint({ title, hint }: { title: string; hint: string }) {
  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 8,
        padding: 32,
        color: 'var(--text-soft)',
        background: 'var(--bg)',
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)' }}>{title}</div>
      <div style={{ fontSize: 12, color: 'var(--text-mute)', maxWidth: 420, textAlign: 'center' }}>
        {hint}
      </div>
    </div>
  );
}
